import { CreditCardOutlined, GlobalOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import { useGetSettingsQuery, useUpdateSettingsMutation } from '@/modules/settings/settingsApi';
import type { SettingsTab } from '@/types/settings';
import { resolveMutationResult } from '@/utils/apiResponse';
import { ContactConfigTab } from './components/ContactConfigTab';
import { PaymentGatewayTab } from './components/PaymentGatewayTab';
import { SmtpConfigTab } from './components/SmtpConfigTab';
import { WebsiteConfigTab } from './components/WebsiteConfigTab';
import styles from './Settings.module.scss';

function resolveTab(value: string | null): SettingsTab {
  if (value === 'contact' || value === 'payment' || value === 'smtp') return value;
  return 'website';
}

export default function Settings() {
  const notify = useNotify();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = resolveTab(searchParams.get('tab'));
  const { data: settings, isLoading } = useGetSettingsQuery();
  const [updateSettings, { isLoading: isSaving }] = useUpdateSettingsMutation();

  useGlobalLoader(isLoading && !settings);

  const setTab = (tab: SettingsTab) => {
    if (tab === 'website') {
      setSearchParams({});
      return;
    }
    setSearchParams({ tab });
  };

  const tabContent = useMemo(() => {
    if (activeTab === 'contact') {
      return (
        <ContactConfigTab
          settings={settings}
          loading={isSaving}
          onSave={async (values) => {
            const result = await resolveMutationResult(updateSettings({ website: values }));
            if (result.ok) {
              notify.success(result.message ?? 'Contact details updated.');
              return;
            }
            notify.error(result.message);
          }}
        />
      );
    }

    if (activeTab === 'payment') {
      return (
        <PaymentGatewayTab
          settings={settings}
          loading={isSaving}
          onSave={async (values) => {
            const result = await resolveMutationResult(updateSettings({ payment: values }));
            if (result.ok) {
              notify.success(result.message ?? 'Payment settings updated.');
              return;
            }
            notify.error(result.message);
          }}
        />
      );
    }

    if (activeTab === 'smtp') {
      return (
        <SmtpConfigTab
          settings={settings}
          loading={isSaving}
          onNotify={(type, message) => {
            if (type === 'success') notify.success(message);
            else notify.error(message);
          }}
          onSave={async (values) => {
            const result = await resolveMutationResult(updateSettings({ smtp: values }));
            if (result.ok) {
              notify.success(result.message ?? 'SMTP settings updated.');
              return;
            }
            notify.error(result.message);
          }}
        />
      );
    }

    return (
      <WebsiteConfigTab
        settings={settings}
        loading={isSaving}
        onSave={async (values) => {
          const result = await resolveMutationResult(updateSettings({ website: values }));
          if (result.ok) {
            notify.success(result.message ?? 'Website settings updated.');
            return;
          }
          notify.error(result.message);
        }}
      />
    );
  }, [activeTab, isSaving, notify, settings, updateSettings]);

  return (
    <div className={styles.page}>
      <PageHeader />

      <div className={styles.layout}>
        <nav className={styles.topTabs} role="tablist" aria-label="Settings">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'website'}
            className={`${styles.topTab} ${activeTab === 'website' ? styles.topTabActive : ''}`}
            onClick={() => setTab('website')}
          >
            <GlobalOutlined />
            Website configuration
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'contact'}
            className={`${styles.topTab} ${activeTab === 'contact' ? styles.topTabActive : ''}`}
            onClick={() => setTab('contact')}
          >
            <PhoneOutlined />
            Contact us
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'payment'}
            className={`${styles.topTab} ${activeTab === 'payment' ? styles.topTabActive : ''}`}
            onClick={() => setTab('payment')}
          >
            <CreditCardOutlined />
            Payment gateway settings
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'smtp'}
            className={`${styles.topTab} ${activeTab === 'smtp' ? styles.topTabActive : ''}`}
            onClick={() => setTab('smtp')}
          >
            <MailOutlined />
            SMTP configuration
          </button>
        </nav>

        <section className={styles.main} role="tabpanel">
          {tabContent}
        </section>
      </div>
    </div>
  );
}
